import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Email, LockOutlined } from '@mui/icons-material';
import Footer from '../../components/Footer/Footer';
import apiClient from '../../services/api-client';
import styles from './RegisterPage.module.css';
import { LocalPhone } from '@mui/icons-material';

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '');

  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const unmaskPhone = (value: string): string => value.replace(/\D/g, '');

const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }

    if (!email.trim()) {
      setError('Email é obrigatório');
      return false;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Email inválido');
      return false;
    }

    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailAlreadyExistsMessage =
      'Este e-mail já está cadastrado. Por favor, utilize outro e-mail ou acesse a página de login.';

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.post('/auth/register', {
        name,
        email,
        phone: unmaskPhone(phone),
        password,
        role: 'reseller',
      });

      setSuccess(true);
      setName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      // Log complete error object
      console.log('Complete error object:', error);
      console.log('Error response:', error.response?.data);
      console.log('Status code:', error.response?.status);

      // Hard code the user exists message based on the 409 Conflict status
      // This handles all scenarios regardless of the exact error format
      if (error.response?.status === 409) {
        setError('EMAIL_ALREADY_EXISTS');
      }
      // Try to handle various error data formats
      else if (error.response?.data?.detail?.code === 'email_already_exists') {
        setError('EMAIL_ALREADY_EXISTS');
      } else if (error.response?.data?.detail?.message) {
        setError(error.response.data.detail.message);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        // For Request failed with status code 409 messages
        if (error.message.includes('409')) {
          setError('EMAIL_ALREADY_EXISTS');
        } else {
          setError(error.response?.data?.detail?.message || emailAlreadyExistsMessage);
        }
      } else {
        setError('Erro ao cadastrar. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <main className={styles.main}>
        {/* Left side - Image */}
        <div className={styles.imageSection}>
          <img
            src="/images/image-19.png"
            alt="People working on computers"
            className={styles.backgroundImage}
          />
        </div>

        {/* Right side - Register Form */}
        <div className={styles.formSection}>
          <Paper elevation={3} className={styles.formContainer}>
            {/* Logos */}
            <div className={styles.logoContainer}>
              <div className={styles.acerLogo}>
                <img src="/images/acer-for-business-logo2.png" alt="Acer for business" />
              </div>
              <div className={styles.cispLogo}>
                <img src="/images/cisp1-logo.png" alt="CISP" />
              </div>
            </div>

            {/* Register Form */}
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.formHeader}>
                <Typography variant="h6" component="h1">
                  Solicite seu acesso!
                </Typography>
                <Typography variant="body2">
                  Preencha seus dados para começar a explorar uma plataforma inteligente feita para
                  facilitar sua rotina, gerar mais vendas e transformar a forma como você compra
                  produtos Acer para revenda.
                </Typography>
              </div>

              {error === 'EMAIL_ALREADY_EXISTS' ? (
                <Alert
                  severity="warning"
                  variant="filled"
                  sx={{
                    mb: 2,
                    backgroundColor: '#ffb74d',
                    '& .MuiAlert-message': {
                      width: '100%',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <AlertTitle sx={{ m: 0, fontWeight: 'bold' }}>
                        Usuário já cadastrado
                      </AlertTitle>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Este e-mail já está cadastrado. Você pode fazer login com suas credenciais ou
                      utilizar a opção de recuperação de senha.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        sx={{
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.1)',
                          },
                        }}
                        onClick={() => navigate('/forgot-password')}
                      >
                        Esqueci minha senha
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: 'white',
                          color: '#e65100',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                          },
                        }}
                        onClick={() => navigate('/login')}
                      >
                        Fazer Login
                      </Button>
                    </Box>
                  </Box>
                </Alert>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  {error}
                </Alert>
              ) : null}

              {success && (
                <div className={styles.successMessage}>
                  Solicitação enviada com sucesso! Um administrador irá revisar seu cadastro.
                </div>
              )}

              <div className={styles.formFields}>
                {/* Name Field */}
                <TextField
                  fullWidth
                  placeholder="Digite seu nome"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  disabled={loading || success}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'var(--acer-green)' }} />
                      </InputAdornment>
                    ),
                    sx: { height: '45px' },
                    inputProps: { style: { fontSize: '15px' } },
                  }}
                />

                {/* Email Field */}
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={loading || success}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'var(--acer-green)' }} />
                      </InputAdornment>
                    ),
                    sx: { height: '45px' },
                    inputProps: { style: { fontSize: '15px' } },
                  }}
                />

                {/* Phone Field */}
                <TextField
                  fullWidth
                  type="tel"
                  placeholder="Digite seu telefone"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  disabled={loading || success}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalPhone sx={{ color: 'var(--acer-green)' }} />
                      </InputAdornment>
                    ),
                    sx: { height: '45px' },
                    inputProps: { style: { fontSize: '15px' }, maxLength: 15 },
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  disabled={loading || success}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'var(--acer-green)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading || success}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ color: 'var(--acer-green)' }} />
                          ) : (
                            <Visibility sx={{ color: 'var(--acer-green)' }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { height: '45px' },
                    inputProps: { style: { fontSize: '15px' } },
                  }}
                />

                {/* Confirm Password Field */}
                <TextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirme sua senha"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading || success}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'var(--acer-green)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={loading || success}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ color: 'var(--acer-green)' }} />
                          ) : (
                            <Visibility sx={{ color: 'var(--acer-green)' }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { height: '45px' },
                    inputProps: { style: { fontSize: '15px' } },
                  }}
                />

                {/* Register Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || success}
                  className={styles.registerButton}
                  sx={{
                    backgroundColor: 'var(--acer-green)',
                    '&:hover': {
                      backgroundColor: 'rgba(131, 184, 26, 0.9)',
                    },
                    height: '45px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  {loading ? 'Enviando...' : 'Solicitar Acesso'}
                </Button>

                {/* Login Link */}
                <div className={styles.loginLink}>
                  <span>Já tem conta? </span>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    className={styles.link}
                    onClick={() => navigate('/login')}
                  >
                    Faça seu login
                  </Link>
                </div>
              </div>

              {/* Already have an account */}
              <div className={styles.loginOptionContainer}>
                <p>Já tem acesso?</p>
                <Button
                  fullWidth
                  variant="outlined"
                  className={styles.loginButton}
                  onClick={() => navigate('/login')}
                >
                  Faça seu login aqui
                </Button>
              </div>
            </form>
          </Paper>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RegisterPage;
